import { Dispatch, SetStateAction } from 'react';
import { DeviceSize, DeviceSizeField } from '@frontend/device-size-field';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { BaseFieldProps, Button, Modal, ModalControlModalProps } from '@frontend/design-system';

type Props = {
  deviceSizeProps: BaseFieldProps<DeviceSize>;
  isUseTemplateButtonVisible?: boolean;
  modalProps: ModalControlModalProps;
  onUseTemplate?: (isEditing: boolean, templateId: string) => void;
  previewHtml?: string;
  setPreviewHtml: Dispatch<SetStateAction<string | undefined>>;
  templateId?: string;
  trackingIdPrefix: string;
};

export const EmailPreviewModal = ({
  deviceSizeProps,
  isUseTemplateButtonVisible = false,
  modalProps,
  onUseTemplate = () => {},
  previewHtml,
  setPreviewHtml,
  templateId,
  trackingIdPrefix,
}: Props) => {
  const { t } = useTranslation('bulk-messaging');

  const handleModalClose = () => {
    modalProps.onClose();
    setPreviewHtml(undefined);
  };

  return (
    <Modal
      {...modalProps}
      onClose={handleModalClose}
      css={{
        border: 0,
        borderRadius: theme.borderRadius.small,
        boxShadow: theme.shadows.floating,
        height: 1200,
        maxHeight: '80vh',
        overflow: 'visible',
        padding: 0,
        transition: 'all 0.2s ease-in-out',
        // TODO: Move desktop and mobile width styles into a constants file
        maxWidth: deviceSizeProps.value === 'desktop' ? 'min(1048px,80vw)' : 375,
        minWidth: deviceSizeProps.value === 'desktop' ? 'min(1048px,80vw)' : 375,
      }}
    >
      <DeviceSizeField
        {...deviceSizeProps}
        trackingIdPrefix={trackingIdPrefix}
        css={{ position: 'absolute', top: theme.spacing(-6), left: 0, button: { border: 0 } }}
      />
      {isUseTemplateButtonVisible && (
        <Button
          css={{ position: 'absolute', top: theme.spacing(-6), right: 50 }}
          size='large'
          label={t('Use This Template')}
          onClick={() => onUseTemplate(false, templateId ?? '')}
        >
          {t('Use This Template')}
        </Button>
      )}
      <Button
        css={{ position: 'absolute', top: theme.spacing(-6), backgroundColor: theme.colors.white, right: 0 }}
        iconName='x'
        size='large'
        label={t('Close Preview')}
        onClick={handleModalClose}
        variant='secondary'
      />
      <iframe
        title={t('Email Preview')}
        srcDoc={previewHtml}
        sandbox='allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox'
        css={{
          border: 0,
          borderRadius: theme.borderRadius.small,
          height: '100%',
          width: '100%',
        }}
      />
    </Modal>
  );
};
